/**
 * The flight screens' one source: today's flight record and the allocation
 * plan, as the `Flight[]` and `StandRegister` the screens' domain was written
 * for — and the way back, row by row.
 *
 * A saved register is not written whole. `registerChanges` turns it into the
 * stands added, the flights released and the flights placed, each sent on
 * its own, so two people working the apron no longer overwrite each other.
 * A placement is forced: the stands screen has already shown the person the
 * conflicts, and they chose. A delay notice goes to the movement with the
 * version the screen read, so a notice on a record that moved on is refused
 * rather than lost.
 *
 * The screens read an operating window, not a calendar day: all of today's
 * service date, plus what of yesterday and tomorrow is still in play, so a
 * late flight does not vanish at midnight and the boards see past it.
 * `today` keeps the day's totals on the day.
 *
 * Errors are thrown with the platform's own message; the screens show them.
 * A record that cannot be read blocks a screen only when there is nothing on
 * it yet; the stand plan failing costs sizes, gates and stands, not the
 * flights.
 */
import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "../../i18n";
import {
  useAllocAddResource,
  useAllocAssign,
  useAllocDay,
  useAllocRelease,
  useAodbDay,
  useAodbNotice,
} from "../../api/queries";
import type { Movement } from "../../domain/aodb";
import type { AllocDay } from "../../domain/allocation";
import type { DelayNotice } from "../../domain/flightDelays";
import { flightName, type Flight } from "../../domain/flights";
import {
  flightsFromRecord,
  inOperatingWindow,
  onPlannedTimes,
  registerChanges,
  registerFromPlan,
  serviceDateToday,
  shiftServiceDate,
} from "../../domain/flightWorld";
import type { StandRegister } from "../../domain/stands";

/** How a register is saved: against what, and whether a refused placement is forced. */
export interface SaveOptions {
  /** the register the change was made from; the live one when left out */
  base?: StandRegister;
  /** false: a refused placement is not written, and the refusal is the error */
  force?: boolean;
}

export interface FlightWorld {
  /** the operating window: today's service date and what of the days either side is in play */
  flights: Flight[];
  /** today's service date only — for the day's totals */
  today: Flight[];
  register: StandRegister;
  day: AllocDay | undefined;
  serviceDate: string;
  loading: boolean;
  /** the record could not be read and there is nothing to show */
  error: string | null;
  /** the stand plan could not be read: no sizes, gates or stands */
  planError: string | null;
  /** a refresh failed; what is on screen is the last good read */
  stale: string | null;
  saveRegister(next: StandRegister, opts?: SaveOptions): Promise<void>;
  recordNotice(flightId: string, n: DelayNotice): Promise<void>;
  savedTo: "platform";
}

const EMPTY: StandRegister = { stands: [], assignments: [] };

const asError = (e: unknown): Error => (e instanceof Error ? e : new Error(String(e)));
const statusOf = (e: unknown) => (e as { status?: number } | null)?.status;

export function useFlightWorld(): FlightWorld {
  const qc = useQueryClient();
  const serviceDate = serviceDateToday(new Date());
  const yesterday = shiftServiceDate(serviceDate, -1);
  const tomorrow = shiftServiceDate(serviceDate, 1);
  const record = useAodbDay(serviceDate);
  const before = useAodbDay(yesterday);
  const after = useAodbDay(tomorrow);
  const alloc = useAllocDay();
  const { mutateAsync: sendNotice } = useAodbNotice();
  const { mutateAsync: assign } = useAllocAssign();
  const { mutateAsync: release } = useAllocRelease();
  const { mutateAsync: addResource } = useAllocAddResource();

  const todays = record.data?.items;
  const yItems = before.data?.items;
  const tItems = after.data?.items;
  const movements = useMemo(() => {
    if (!todays) return undefined;
    const now = new Date();
    const seen = new Set<string>();
    const out: Movement[] = [];
    for (const m of [...(yItems ?? []), ...todays, ...(tItems ?? [])]) {
      if (seen.has(m.id) || !inOperatingWindow(m, serviceDate, now)) continue;
      seen.add(m.id);
      out.push(m);
    }
    return out;
  }, [todays, yItems, tItems, serviceDate]);
  const day = alloc.data;
  const flights = useMemo(() => flightsFromRecord(movements ?? [], day), [movements, day]);
  const today = useMemo(() => {
    const ids = new Set((todays ?? []).map((m) => m.id));
    return flights.filter((f) => ids.has(f.id));
  }, [flights, todays]);
  /* the stays as planned: the screens' domain adds the delays back itself */
  const register = useMemo(
    () => (day ? onPlannedTimes(registerFromPlan(day, new Date()), flights) : EMPTY),
    [day, flights],
  );

  const saveRegister = useCallback(
    async (next: StandRegister, opts: SaveOptions = {}) => {
      if (!day) throw new Error(t("The stand plan is not available, so nothing was saved."));
      const force = opts.force ?? true;
      const skipped: string[] = [];
      try {
        for (const op of registerChanges(opts.base ?? register, next, day)) {
          if (op.kind === "add") await addResource(op.resource);
          else if (op.kind === "release") await release({ key: op.key });
          else if (op.kind === "assign") {
            const r = await assign({ key: op.key, codes: [op.code], force });
            /* not forced and refused: nothing was written — say why */
            if (!force && r.refused?.length && !r.learned) throw new Error(r.refused.map((x) => x.why).join(" "));
          } else {
            const f = flights.find((x) => x.id === op.flightId);
            skipped.push(f ? flightName(f) : op.flightId);
          }
        }
      } catch (e) {
        throw asError(e);
      }
      if (skipped.length) throw new Error(t("{flights} not on the stand plan yet, so not placed.", { flights: skipped.join(", ") }));
    },
    [day, register, flights, addResource, release, assign],
  );

  const recordNotice = useCallback(
    async (flightId: string, n: DelayNotice) => {
      const m = movements?.find((x) => x.id === flightId);
      if (!m) throw new Error(t("That flight is not on the record."));
      const send = (version: number) =>
        sendNotice({ movementId: m.id, source: n.source, newTime: n.newTime, code: n.code, note: n.note, version });
      try {
        await send(m.version);
      } catch (e) {
        if (statusOf(e) !== 409) throw asError(e);
        /*
         * The record moved on since the screen read it. A notice only adds to
         * it, so read the movement again and send once more on the new version.
         */
        await qc.invalidateQueries({ queryKey: ["aodb", "day"] });
        const fresh = (qc.getQueriesData<{ items: Movement[] }>({ queryKey: ["aodb", "day"] }) ?? [])
          .flatMap(([, v]) => v?.items ?? [])
          .find((x) => x.id === m.id);
        if (!fresh || fresh.version === m.version) throw asError(e);
        try {
          await send(fresh.version);
        } catch (e2) {
          throw asError(e2);
        }
      }
    },
    [movements, sendNotice, qc],
  );

  /* a read with something already on screen keeps it; the failure is a note, not the page */
  const blocking = !todays && record.error;
  const refreshFailed = todays && record.error;
  return {
    flights,
    today,
    register,
    day,
    serviceDate,
    loading: record.isLoading || alloc.isLoading,
    error: blocking ? asError(blocking).message : null,
    planError: alloc.error ? asError(alloc.error).message : null,
    stale: refreshFailed ? asError(refreshFailed).message : null,
    saveRegister,
    recordNotice,
    savedTo: "platform",
  };
}
